import { API_URLS } from '../../../routes/urls';
import { Institute, InstituteListItem } from '../../../types/institute';
import { PaginatedListParams, PaginatedListResponse, StrOrNum } from '../../../types/util';
import { apiSlice } from '../../apiSlice';
import { transformPaginatedResponseFilters } from '../../../components/paginated-list/helper';
import {
  CreateInstituteForStudentEducationPayload,
  CreateInstituteForStudentEducationResponse,
  EligibleInstitutesDataResponse,
  TransformedEligibleInstitutesDataResponse,
} from './types';
import { generatePath } from 'react-router-dom';
import { convertObjectToFormData } from '../../../utils/form';
import { educationApi } from '../education/educationSlice';
import { QueryErrorReturnValue } from '../../types';
import { addSnackbar } from '../snackbar/snackbarSlice';

const instituteApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listInstitutes: builder.query<PaginatedListResponse<InstituteListItem>, PaginatedListParams | void>({
      query: (params) => ({
        url: API_URLS.institutes,
        params: {
          ...params,
          limit: 12,
        },
      }),
      transformResponse: (response: PaginatedListResponse<InstituteListItem>) =>
        transformPaginatedResponseFilters(response),
    }),

    createInstituteForStudentEducation: builder.mutation<
      CreateInstituteForStudentEducationResponse,
      CreateInstituteForStudentEducationPayload
    >({
      query: ({ slug, ...otherInputs }) => ({
        url: generatePath(API_URLS.createInstituteForEducation, { slug }),
        method: 'POST',
        data: convertObjectToFormData(otherInputs as unknown as Record<string, StrOrNum | StrOrNum[]>),
      }),
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          await queryFulfilled;
          dispatch(
            educationApi.util.updateQueryData('fetchEducationFormOptions', undefined, (draft) => {
              draft.institute.push({
                label: data.name,
                value: data.id,
                helperText: data.address,
              });
            })
          );
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          if (err.error.status !== 422) {
            dispatch(
              addSnackbar({
                message: err.error.message,
                severity: 'error',
              })
            );
          }
        }
      },
    }),

    singleInstitute: builder.query<Institute, string>({
      query: (instituteSlug) => ({
        url: generatePath(API_URLS.singleInstitute, { instituteSlug }),
      }),
    }),

    featuredInstitutes: builder.query<InstituteListItem[], void>({
      query: () => ({
        url: API_URLS.featuredInstitutes,
      }),
    }),

    eligibleInstitutesData: builder.query<TransformedEligibleInstitutesDataResponse, PaginatedListParams>({
      query: (params) => ({
        url: API_URLS.eligibleInstitutesData,
        params,
      }),
      transformResponse: (response: EligibleInstitutesDataResponse): TransformedEligibleInstitutesDataResponse => {
        const transformedResponse: TransformedEligibleInstitutesDataResponse = {
          ...transformPaginatedResponseFilters(response),
          instituteIds: response.institutesData.map((data) => data.id),
        };
        return transformedResponse;
      },
      providesTags: ['EligibleModule'],
    }),

    institutesByIds: builder.query<InstituteListItem[], StrOrNum[]>({
      query: (ids) => ({
        url: API_URLS.institutesByIds,
        method: 'POST',
        data: convertObjectToFormData({ ids }),
      }),
    }),
  }),
});

export const {
  useListInstitutesQuery,
  useCreateInstituteForStudentEducationMutation,
  useSingleInstituteQuery,
  useFeaturedInstitutesQuery,
  useEligibleInstitutesDataQuery,
  useLazyInstitutesByIdsQuery,
} = instituteApi;
